import React, { useState, useMemo } from 'react';
import { Modal, Button, Input } from '../ui';
import { useCreateUserMutation } from '../../hooks/voice-demo/useCreateUserMutation';
import type { PartnerUser } from '../../hooks/requests/queries/use_all_users_query';
import { COUNTRIES } from '../../constants/countries';
import Alert from '../ui/Alert';

// Must be defined outside the component to avoid "more hooks than previous render" error
interface SelectFieldProps {
  label?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  children: React.ReactNode;
  required?: boolean;
}

const SelectField: React.FC<SelectFieldProps> = ({
  label,
  value,
  onChange,
  children,
  required,
}) => (
  <div className="w-full">
    {label && (
      <label className="block text-sm font-medium text-au-text-primary mb-1.5">
        {label}
        {required && ' *'}
      </label>
    )}
    <div className="relative">
      <select
        value={value}
        onChange={onChange}
        className="au-input w-full cursor-pointer appearance-none pr-8"
      >
        {children}
      </select>
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2.5 text-au-text-tertiary">
        <svg
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  </div>
);

const LANGUAGE_OPTIONS = [
  { value: 'english', label: 'English (US)' },
  { value: 'english_uk', label: 'English (UK)' },
  { value: 'english_australia', label: 'English (Australia)' },
  { value: 'english_belgium', label: 'English (Belgium)' },
  { value: 'english_canada', label: 'English (Canada)' },
  { value: 'english_france', label: 'English (France)' },
  { value: 'english_germany', label: 'English (Germany)' },
  { value: 'english_global_neutral', label: 'English (Global Neutral)' },
  { value: 'english_india', label: 'English (India)' },
  { value: 'english_netherlands', label: 'English (Netherlands)' },
  { value: 'english_sweden', label: 'English (Sweden)' },
  { value: 'spanish', label: 'Spanish' },
  { value: 'spanish_mexico', label: 'Spanish (Mexico)' },
  { value: 'portuguese', label: 'Portuguese' },
  { value: 'portuguese_brazil', label: 'Portuguese (Brazil)' },
  { value: 'french', label: 'French' },
  { value: 'french_belgium', label: 'French (Belgium)' },
  { value: 'french_canada', label: 'French (Canada)' },
  { value: 'dutch', label: 'Dutch' },
  { value: 'dutch_belgium', label: 'Dutch (Belgium)' },
  { value: 'simplified_chinese_china', label: 'Chinese (Simplified)' },
  { value: 'danish_denmark', label: 'Danish' },
  { value: 'german_germany', label: 'German' },
  { value: 'italian_italy', label: 'Italian' },
  { value: 'japanese_japan', label: 'Japanese' },
  { value: 'korean_south_korea', label: 'Korean' },
  { value: 'polish_poland', label: 'Polish' },
  { value: 'swedish_sweden', label: 'Swedish' },
];

function generateId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  users: PartnerUser[];
}

const CreateUserModal: React.FC<CreateUserModalProps> = ({ isOpen, onClose, users }) => {
  const [firstName, setFirstName] = useState('');
  const [userId, setUserId] = useState(() => generateId());
  const [companySelection, setCompanySelection] = useState('');
  const [newCompanyId, setNewCompanyId] = useState(() => generateId());
  const [newCompanyName, setNewCompanyName] = useState('');
  const [language, setLanguage] = useState('english');
  const [country, setCountry] = useState('');
  const [error, setError] = useState('');

  const createUserMutation = useCreateUserMutation();

  const companyOptions = useMemo(() => {
    const seen = new Map<string, string>();
    users.forEach((u) => {
      if (!seen.has(u.company_id)) {
        seen.set(u.company_id, u.company_name || u.company_id);
      }
    });
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [users]);

  const isNewCompany = companySelection === '__new__';

  const resetForm = () => {
    setFirstName('');
    setUserId(generateId());
    setCompanySelection('');
    setNewCompanyId(generateId());
    setNewCompanyName('');
    setLanguage('english');
    setCountry('');
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    setError('');

    if (!firstName.trim()) {
      setError('First name is required.');
      return;
    }
    if (!userId.trim()) {
      setError('User ID is required.');
      return;
    }
    if (!companySelection) {
      setError('Please select or create a company.');
      return;
    }
    if (!country) {
      setError('Country is required.');
      return;
    }

    let companyId: string;
    let companyName: string;

    if (isNewCompany) {
      if (!newCompanyId.trim()) {
        setError('Company ID is required.');
        return;
      }
      if (!newCompanyName.trim()) {
        setError('Company name is required.');
        return;
      }
      companyId = newCompanyId.trim();
      companyName = newCompanyName.trim();
    } else {
      companyId = companySelection;
      companyName = companyOptions.find((c) => c.id === companySelection)?.name || companySelection;
    }

    try {
      await createUserMutation.mutateAsync({
        userId: userId.trim(),
        firstName: firstName.trim(),
        companyId,
        companyName,
        language,
        country,
      });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create user');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="New user"
      size="lg"
      footer={
        <div className="flex gap-3 justify-end">
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} isLoading={createUserMutation.isPending}>
            Create user
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="First name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="Jane"
            required
          />
          <div>
            <Input
              label="User ID"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              placeholder="auto-generated"
              required
            />
            <p className="text-xs text-au-text-tertiary mt-1">
              Unique identifier from your system.
            </p>
          </div>
        </div>

        <SelectField
          label="Company"
          required
          value={companySelection}
          onChange={(e) => setCompanySelection(e.target.value)}
        >
          <option value="">Select a company...</option>
          {companyOptions.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
          <option value="__new__">+ New company...</option>
        </SelectField>

        {isNewCompany && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Company ID"
              value={newCompanyId}
              onChange={(e) => setNewCompanyId(e.target.value)}
              placeholder="auto-generated"
              required
            />
            <Input
              label="Company name"
              value={newCompanyName}
              onChange={(e) => setNewCompanyName(e.target.value)}
              placeholder="Acme Corp"
              required
            />
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SelectField
            label="Language"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
          >
            {LANGUAGE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </SelectField>
          <SelectField
            label="Country"
            required
            value={country}
            onChange={(e) => setCountry(e.target.value)}
          >
            <option value="">Select a country...</option>
            {COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.name}
              </option>
            ))}
          </SelectField>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}
      </div>
    </Modal>
  );
};

export default CreateUserModal;
